import { useEffect, useRef, useState } from "react";
import vwr, { type VwrInstance, type VwrOptions } from "vwr.js";
import { FileWarning } from "lucide-react";

type VwrErrorEvent = Parameters<NonNullable<VwrOptions["onError"]>>[0];

interface FeeAgreementViewerProps {
  bookingId: string;
  height?: string;
  className?: string;
}

export function FeeAgreementViewer({
  bookingId,
  height = "640px",
  className = "",
}: FeeAgreementViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<VwrInstance | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId || !containerRef.current) return;

    const abortController = new AbortController();
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch {
        /* ignore */
      }
      viewerRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (containerRef.current) containerRef.current.innerHTML = "";

    const loadDocument = async () => {
      try {
        const response = await fetch(`/fee_agreement_pdf/${bookingId}`, {
          credentials: "include",
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? "Fee agreement not found"
              : "Couldn't load the fee agreement",
          );
        }
        const blob = await response.blob();
        if (cancelled || !containerRef.current) return;

        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;

        viewerRef.current = vwr.render(containerRef.current, blobUrl, {
          onLoad: () => {
            if (!cancelled) setIsLoading(false);
          },
          onError: (err: VwrErrorEvent) => {
            if (cancelled) return;
            setError(err.message || "Failed to load fee agreement");
            setIsLoading(false);
          },
        });

        setTimeout(() => {
          if (!cancelled) setIsLoading(false);
        }, 2000);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.name === "AbortError") return;
        setError(
          err instanceof Error ? err.message : "Failed to load fee agreement",
        );
        setIsLoading(false);
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      abortController.abort();
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch {
          /* ignore */
        }
        viewerRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [bookingId]);

  if (error) {
    return (
      <div
        className={`bg-paper-hi rounded-lg shadow-card flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center px-6 max-w-sm">
          <div className="w-10 h-10 rounded-full bg-rust-s/60 mx-auto flex items-center justify-center text-rust mb-3">
            <FileWarning size={18} strokeWidth={1.8} />
          </div>
          <p className="text-[14px] font-medium text-ink-2">
            Couldn't load the fee agreement
          </p>
          <p className="text-[12px] text-ink-4 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative bg-paper-hi rounded-lg shadow-card overflow-hidden ${className}`}
      style={{ height }}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-paper-hi/90 backdrop-blur-sm">
          <span className="text-[12.5px] text-ink-4">
            Loading fee agreement…
          </span>
        </div>
      )}
      <div
        ref={containerRef}
        className={`w-full h-full transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
      />
    </div>
  );
}
